import React, { useState } from 'react';

import { Button, Spinner, Text } from '@change/ds-core-ui';

import { Translate, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/petitionDetails/petition-details.yml';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { useEndorsements } from '#src/app/shared/components/endorsements';
import { ENDORSEMENTS_HASH, ENDORSEMENTS_SECTION_ID } from '#src/app/shared/constants';
import { useScrollToHash } from '#src/app/shared/hooks/scroll';

import { ScrollTrackingWaypointContainer } from '../ScrollTrackingWaypoint';

import { EndorsementsList } from './components/EndorsementsList';
import { EndorsementsModal } from './components/EndorsementsModal';
import { useTracking } from './hooks/useTracking';

const SCROLL_OFFSET_PX = 40;

export const EndorsementsContainerComponent: React.FC = () => {
	const [isEndorsementsModalOpen, setIsEndorsementsModalOpen] = useState(false);
	const [endorsementIdToScrollTo, setEndorsementIdToScrollTo] = useState<string | null>(null);

	const { trackPetitionFullEndorsementClick, trackPetitionEndorsementsViewAllClick } = useTracking();

	const {
		data: { petition },
	} = usePetitionDetailsPageData();

	const {
		data: { endorsements, isLoading, error },
	} = useEndorsements({ petitionSlugOrId: petition.id });

	const handleFullEndorsementClick = (endorsementId?: string) => {
		trackPetitionFullEndorsementClick(petition.id);
		setIsEndorsementsModalOpen(true);
		setEndorsementIdToScrollTo(endorsementId ?? null);
	};

	const handleViewAllEndorsementsClick = () => {
		trackPetitionEndorsementsViewAllClick(petition.id);
		setIsEndorsementsModalOpen(true);
	};

	const handleCloseEndorsementsModal = () => {
		setIsEndorsementsModalOpen(false);
		setEndorsementIdToScrollTo(null);
	};

	useScrollToHash({
		elementId: ENDORSEMENTS_SECTION_ID,
		targetHash: ENDORSEMENTS_HASH,
		enabled: !isLoading && endorsements.length > 0,
		offset: SCROLL_OFFSET_PX,
	});

	if (isLoading) {
		return (
			<>
				<ScrollTrackingWaypointContainer name="scroll_loading_endorsements_section" />
				<Spinner className="mx-auto my-16" />
			</>
		);
	}

	if (error) {
		return <div className="text-typography-weak">Failed to load endorsements</div>;
	}

	if (endorsements.length === 0) {
		return null;
	}

	return (
		// eslint-disable-next-line @change-ds/ds-no-arbitrary-spacing-classname
		<div id={ENDORSEMENTS_SECTION_ID} className="mt-20 mb-[52px] flex flex-col md:mb-20">
			<ScrollTrackingWaypointContainer name="scroll_endorsements_section" />
			<Text className="text-size-heading-small md:text-size-heading-large">
				<Translate value="corgi.petition.details.endorsements.heading" />
			</Text>
			<Text variant="body" size="large" className="text-typography-weak mt-4 leading-7">
				<Translate value="corgi.petition.details.endorsements.subheading" />
			</Text>
			<div className="mt-6 mb-6 md:mt-8">
				<EndorsementsList
					onOpenEndorsementsModal={handleFullEndorsementClick}
					endorsements={endorsements}
					petitionId={petition.id}
				/>
			</div>

			<EndorsementsModal
				isOpen={isEndorsementsModalOpen}
				petitionId={petition.id}
				onClose={handleCloseEndorsementsModal}
				setIsOpen={setIsEndorsementsModalOpen}
				endorsements={endorsements}
				endorsementIdToScrollTo={endorsementIdToScrollTo}
				onScrolledToEndorsement={() => setEndorsementIdToScrollTo(null)}
			/>

			{endorsements.length > 3 && (
				<Button
					variant="secondary"
					size="small"
					data-qa="view-all-endorsements-button"
					data-testid="view-all-endorsements-button"
					onClick={handleViewAllEndorsementsClick}
				>
					<Translate value="corgi.petition.details.endorsements.view_all_button" />
				</Button>
			)}

			<Text variant="body" size="small" className="text-typography-weak mt-6">
				<Translate value="corgi.petition_details.endorsements.legal_disclaimer" />
			</Text>
		</div>
	);
};

export const EndorsementsContainer = withI18nPlaceholders(I18N_PLACEHOLDERS, EndorsementsContainerComponent);
